import {
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Edit,
  Flag,
  Link2,
  Loader2,
  MoreHorizontal,
  Pin,
  Star,
  Trash,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useClickOutside } from "@/hooks/useClickOutside";
import { cn } from "@/lib/utils";

interface PostCardHeaderActionsProps {
  // Favorite
  isFavorited?: boolean;
  isFavoriting?: boolean;
  postContentId: number;
  onToggleFavorite?: (
    postContentId: number,
    isCurrentlyFavorited: boolean,
  ) => Promise<void>;
  onCopyLink?: () => void;

  // Menu items
  isCurrentUserAuthor?: boolean;
  isGroupAdmin?: boolean;
  isPinned?: boolean;
  isMenuDisabled?: boolean;
  isDeleteDisabled?: boolean;
  hideOptionsButton?: boolean;

  // Minimize
  isMinimized?: boolean;
  onToggleMinimize?: () => void;

  onReportClick?: () => void;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  onPinClick?: () => void;
  canMoveChannel?: boolean;
  onMoveChannelClick?: () => void;
}

export const PostCardHeaderActions: React.FC<PostCardHeaderActionsProps> = ({
  isFavorited,
  isFavoriting,
  postContentId,
  onToggleFavorite,
  onCopyLink,
  isCurrentUserAuthor,
  isGroupAdmin,
  isPinned,
  isMenuDisabled,
  isDeleteDisabled,
  hideOptionsButton,
  isMinimized,
  onToggleMinimize,
  onReportClick,
  onEditClick,
  onDeleteClick,
  onPinClick,
  canMoveChannel,
  onMoveChannelClick,
}) => {
  const { t } = useTranslation();
  const [localFavorited, setLocalFavorited] = useState(isFavorited);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useClickOutside(() => setShowMenu(false), menuRef, menuButtonRef);

  useEffect(() => {
    setLocalFavorited(isFavorited);
  }, [isFavorited]);

  const handleStarClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onToggleFavorite) return;

    const currentState = localFavorited || false;
    setLocalFavorited(!currentState);
    try {
      await onToggleFavorite(postContentId, currentState);
    } catch (error) {
      setLocalFavorited(isFavorited); // Revert on error
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu((prev) => !prev);
  };

  const createMenuClickHandler =
    (handler?: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setShowMenu(false);
      handler?.();
    };

  const canCopyLink = Boolean(onCopyLink);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1">
        {onToggleFavorite && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="flex size-8 items-center justify-center rounded-full bg-white"
                data-no-navigate
                onClick={handleStarClick}
                disabled={isFavoriting}
                aria-label={
                  localFavorited
                    ? t("common.unfavorite", {
                        defaultValue: "Remove from favorites",
                      })
                    : t("common.favorite", { defaultValue: "Add to favorites" })
                }
              >
                {isFavoriting ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Star
                    className={cn(
                      "h-5 w-5",
                      localFavorited
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-rw-dark-gray",
                    )}
                    strokeWidth={2}
                  />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {localFavorited
                ? t("common.unfavorite", {
                    defaultValue: "Remove from favorites",
                  })
                : t("common.favorite", { defaultValue: "Add to favorites" })}
            </TooltipContent>
          </Tooltip>
        )}

        {onToggleMinimize && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="flex size-8 items-center justify-center rounded-full bg-white"
                data-no-navigate
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleMinimize();
                }}
                aria-label={
                  isMinimized
                    ? t("common.unminimize", { defaultValue: "Restore" })
                    : t("common.minimize", { defaultValue: "Minimize" })
                }
              >
                {isMinimized ? (
                  <ChevronDown className="size-5 text-rw-dark-gray" />
                ) : (
                  <ChevronUp className="size-5 text-rw-dark-gray" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isMinimized
                ? t("common.unminimize", { defaultValue: "Restore" })
                : t("common.minimize", { defaultValue: "Minimize" })}
            </TooltipContent>
          </Tooltip>
        )}

        {!hideOptionsButton && (
          <div className="relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={handleToggleMenu}
                  ref={menuButtonRef}
                  disabled={isMenuDisabled}
                  className="flex size-8 items-center justify-center rounded-full bg-white"
                  data-no-navigate
                  aria-label={t("common.options", { defaultValue: "Options" })}
                >
                  <MoreHorizontal />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.options", { defaultValue: "Options" })}
              </TooltipContent>
            </Tooltip>

            {showMenu && !isMenuDisabled && (
              <div
                ref={menuRef}
                className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                data-no-navigate
              >
                {canCopyLink && (
                  <Button
                    variant="ghost"
                    onClick={createMenuClickHandler(onCopyLink)}
                    className="w-full justify-start"
                  >
                    <Link2 size={14} className="mr-2" /> {t("common.copyLink")}
                  </Button>
                )}
                {!isCurrentUserAuthor && onReportClick && (
                  <Button
                    variant="ghost"
                    onClick={createMenuClickHandler(onReportClick)}
                    className="w-full justify-start"
                  >
                    <Flag size={14} className="mr-2" /> {t("common.report")}
                  </Button>
                )}

                {isCurrentUserAuthor && (
                  <>
                    {onEditClick && (
                      <Button
                        variant="ghost"
                        onClick={createMenuClickHandler(onEditClick)}
                        className="w-full justify-start"
                      >
                        <Edit size={14} className="mr-2" /> {t("common.edit")}
                      </Button>
                    )}
                    {onDeleteClick && (
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={createMenuClickHandler(onDeleteClick)}
                        disabled={isDeleteDisabled}
                      >
                        <Trash size={14} className="mr-2" />{" "}
                        {t("common.delete")}
                      </Button>
                    )}
                  </>
                )}

                {isGroupAdmin && onPinClick && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={createMenuClickHandler(onPinClick)}
                  >
                    <Pin size={14} className="mr-2" />{" "}
                    {isPinned ? t("common.unpinPost") : t("common.pinPost")}
                  </Button>
                )}

                {canMoveChannel && onMoveChannelClick && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={createMenuClickHandler(onMoveChannelClick)}
                  >
                    <ArrowRightLeft size={14} className="mr-2" />
                    {t("common.moveChannel", { defaultValue: "Move channel" })}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
